
/**
 * Допоміжні форми гостьових акаунтів: запит пароля (відв'язка Telegram тощо), збереження пароля в менеджері, валідатори.
 * Реєстрація й відновлення пароля тепер вбудовані прямо у форму авторизації (pages/more.ts).
 * Розмітка будується тут і додається в DOM лише коли потрібна. Валідація на клієнті дзеркалить серверну
 * (src/lib/subs-users.ts), але сервер перевіряє все сам.
 *
 * Стабільність (форми колись "їздили" і в поля було важко потрапити):
 *  - це ОДНЕ вікно на всю послідовність кроків — між кроками міняється лише вміст, вікно не перестворюється;
 *  - вікно прив'язане до верху, а не центроване: коли міняється висота чи з'являється клавіатура, воно не стрибає;
 *  - власний прокручуваний шар + блокування прокрутки сторінки під ним;
 *  - поля ≥16px — інакше iOS Safari масштабує сторінку при фокусі;
 *  - місце під повідомлення про помилку зарезервоване — поява тексту не зсуває кнопки;
 *  - на сенсорних екранах поле не фокусується саме (клавіатура не з'являється без дотику).
 *
 * Паролі: це справжні <form> з name/autocomplete (username / new-password / one-time-code) — Google Password
 * Manager (і Safari/Firefox) пропонує надійний пароль при створенні акаунта й пропонує зберегти/оновити його.
 */

interface FieldSpec {
  key: string;
  label: string;
  type: 'text' | 'password';
  name: string;
  autocomplete: string;
  maxLength?: number;
  value?: string;
  inputMode?: 'numeric';
  /** Поле існує (для менеджера паролів), але користувач його не бачить. */
  hidden?: boolean;
}

interface StepSpec {
  title: string;
  message?: string;
  fields: FieldSpec[];
  submitText: string;
  /** Додаткові кнопки між "Скасувати" і головною. */
  extra?: Array<{ text: string; onClick: () => void }>;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}

interface Stepper {
  go(step: StepSpec): void;
  close(): void;
}

/**
 * Просимо браузер (Chrome/Google Password Manager) зберегти або оновити пароль явно. Автовиявлення за <form>
 * не завжди спрацьовує, бо запит іде через fetch, а сторінка одразу перезавантажується.
 */
export async function rememberCredentials(login: string, password: string): Promise<void> {
  try {
    const PC = (window as unknown as { PasswordCredential?: new (d: { id: string; password: string; name?: string }) => Credential }).PasswordCredential;
    if (PC && navigator.credentials?.store) await navigator.credentials.store(new PC({ id: login, password, name: login }));
  } catch {
    /* не підтримується / користувач відмовився — не критично */
  }
}

const coarsePointer = () => window.matchMedia?.('(pointer: coarse)').matches ?? false;

function openStepper(opts: { formName: string; formAction: string; first: StepSpec }): Stepper {
  const overlay = document.createElement('div');
  overlay.className = 'account-overlay';
  const box = document.createElement('div');
  box.className = 'account-modal';
  const form = document.createElement('form');
  form.name = opts.formName;
  form.action = opts.formAction;
  form.method = 'post';
  form.noValidate = true;
  const title = document.createElement('h3');
  title.className = 'modal-title';
  const message = document.createElement('p');
  message.className = 'modal-message';
  const fieldsBox = document.createElement('div');
  fieldsBox.className = 'account-fields';
  const errorEl = document.createElement('p');
  errorEl.className = 'account-msg err';
  const actions = document.createElement('div');
  actions.className = 'modal-actions';
  form.append(title, message, fieldsBox, errorEl, actions);
  box.appendChild(form);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  document.body.classList.add('modal-open');

  let current: StepSpec = opts.first;
  let inputs: Record<string, HTMLInputElement> = {};
  let okBtn: HTMLButtonElement;

  function close() {
    document.removeEventListener('keydown', onKey);
    document.body.classList.remove('modal-open');
    overlay.remove();
  }

  function render(step: StepSpec) {
    current = step;
    title.textContent = step.title;
    message.textContent = step.message ?? '';
    message.style.display = step.message ? 'block' : 'none';
    errorEl.textContent = '';
    fieldsBox.innerHTML = '';
    inputs = {};
    let visibleIndex = 0;
    for (const f of step.fields) {
      const input = document.createElement('input');
      input.type = f.type;
      input.name = f.name;
      input.id = `acc-${opts.formName}-${f.name}-${f.key}`;
      input.setAttribute('autocomplete', f.autocomplete);
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('autocorrect', 'off');
      input.spellcheck = false;
      if (f.maxLength) input.maxLength = f.maxLength;
      if (f.inputMode) input.inputMode = f.inputMode;
      if (f.value !== undefined) input.value = f.value;
      inputs[f.key] = input;
      if (f.hidden) {
        // Не display:none — менеджер паролів ігнорує такі поля. Невидиме, але в потоці форми.
        input.className = 'account-hidden-field';
        input.tabIndex = -1;
        input.setAttribute('aria-hidden', 'true');
        fieldsBox.appendChild(input);
        continue;
      }
      const label = document.createElement('label');
      label.className = 'field-block' + (visibleIndex++ ? ' login-field-gap' : '');
      const span = document.createElement('span');
      span.className = 'field-block-label';
      span.textContent = f.label;
      input.className = 'field-block-input';
      label.append(span, input);
      fieldsBox.appendChild(label);
    }

    actions.innerHTML = '';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn';
    cancel.textContent = 'Скасувати';
    cancel.addEventListener('click', close);
    actions.appendChild(cancel);
    for (const a of step.extra ?? []) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'btn';
      b.textContent = a.text;
      b.addEventListener('click', a.onClick);
      actions.appendChild(b);
    }
    okBtn = document.createElement('button');
    okBtn.type = 'submit';
    okBtn.className = 'btn btn-accent';
    okBtn.textContent = step.submitText;
    actions.appendChild(okBtn);

    // Комп'ютер: одразу в перше поле. Телефон: ні — клавіатура сама не з'являється й не зсуває вікно.
    if (!coarsePointer()) Object.values(inputs).find((i) => i.className !== 'account-hidden-field')?.focus();
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const values: Record<string, string> = {};
    for (const [k, el] of Object.entries(inputs)) values[k] = el.value;
    errorEl.textContent = '';
    okBtn.disabled = true;
    const step = current;
    try {
      await step.onSubmit(values);
    } catch (err) {
      if (current === step) errorEl.textContent = (err as Error).message || 'Щось пішло не так. Спробуйте пізніше.';
    } finally {
      if (okBtn.isConnected) okBtn.disabled = false;
    }
  });
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);

  render(opts.first);
  return { go: render, close };
}

/** Показує помилку валідації без запиту: кидає виняток, який форма виводить у зарезервований рядок. */
function fail(text: string): never {
  throw new Error(text);
}

// ---------------- валідація (дзеркало src/lib/subs-users.ts; сервер перевіряє все сам) ----------------

const NICK_RE = /^[\p{L}\p{N}][\p{L}\p{N}_.-]*[\p{L}\p{N}]$/u;
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]{2,}$/;

export function checkNickname(nick: string): string | null {
  if (nick.length < 3 || nick.length > 32) return 'Нікнейм має містити від 3 до 32 символів.';
  if (!NICK_RE.test(nick)) return 'Нікнейм: літери, цифри та _ . - (починається й закінчується літерою чи цифрою).';
  if (/\p{Script=Latin}/u.test(nick) && /\p{Script=Cyrillic}/u.test(nick)) return 'Не змішуйте латиницю й кирилицю в одному нікнеймі.';
  return null;
}

export function checkEmail(email: string): string | null {
  if (!email) return 'Вкажіть email.';
  if (email.length > 254 || !EMAIL_RE.test(email)) return 'Вкажіть коректний email.';
  return null;
}

export function checkPassword(password: string, confirm?: string): string | null {
  if (password.length < 8) return 'Пароль має містити щонайменше 8 символів.';
  if (password.length > 128) return 'Пароль задовгий (максимум 128 символів).';
  if (confirm !== undefined && password !== confirm) return 'Пароль і підтвердження не збігаються.';
  return null;
}

// ---------------- запит пароля (відв'язка Telegram тощо) ----------------

export function askPassword(opts: { title: string; message: string; submitText: string; onSubmit: (password: string) => Promise<void> }): void {
  const flow = openStepper({
    formName: 'confirm-password',
    formAction: '#',
    first: {
      title: opts.title,
      message: opts.message,
      fields: [{ key: 'password', label: 'Поточний пароль', type: 'password', name: 'current-password', autocomplete: 'current-password', maxLength: 128 }],
      submitText: opts.submitText,
      async onSubmit(v) {
        if (!v.password) fail('Введіть пароль.');
        await opts.onSubmit(v.password);
        flow.close();
      },
    },
  });
}
